import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCreateProductMutation } from 'api/productApi';
import Layout from 'layout/Layout';
import Swal from 'sweetalert2';

export const CreateProduct = () => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: '',
    price1: '',
    unit1: '',
    price2: '',
    unit2: '',
    price3: '',
    unit3: '',
    image: null,
  });

  const [errors, setErrors] = useState({});
  const [imagePreview, setImagePreview] = useState(null);
  const [createProduct, { isLoading }] = useCreateProductMutation();
  const navigate = useNavigate();

  const handleChange = e => {
    const { name, value, files } = e.target;
    if (name === 'image' && files.length > 0) {
      setImagePreview(URL.createObjectURL(files[0]));
    }

    setFormData(prev => ({
      ...prev,
      [name]: files ? files[0] : value,
    }));
    
    // Clear error on change
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    const requiredFields = ['name', 'description', 'category', 'price1', 'unit1'];
    
    requiredFields.forEach(field => {
      if (!formData[field]) {
        newErrors[field] = 'This field is required';
      }
    });

    if (!formData.image) {
      newErrors.image = 'Product image is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async e => {
    e.preventDefault();
    
    if (!validateForm()) {
      Swal.fire({
        icon: 'error',
        title: 'Validation Error',
        text: 'Please fill in all required fields.',
        confirmButtonColor: '#d33',
      });
      return;
    }

    const payload = new FormData();
    for (let key in formData) {
      if (formData[key]) {
        payload.append(key, formData[key]);
      }
    }
    
    try {
      await createProduct(payload).unwrap();
      Swal.fire({
        title: 'Success!',
        text: 'Product created successfully.',
        icon: 'success',
        confirmButtonColor: '#10b981',
      }).then(() => {
        navigate('/product');
      });
    } catch (err) {
      console.error('Product creation failed', err);
      Swal.fire({
        title: 'Error!',
        text: 'Failed to create product.',
        icon: 'error',
        confirmButtonColor: '#d33',
      });
    }
  };

  const InputField = ({ label, name, type = 'text', placeholder, isRequired = false, isTextarea = false }) => (
    <div className="flex flex-col">
      <label className="mb-1 text-sm font-semibold text-gray-700">
        {label} {isRequired && <span className="text-red-500">*</span>}
      </label>
      {isTextarea ? (
        <textarea
          rows={4}
          name={name}
          value={formData[name]}
          onChange={handleChange}
          placeholder={placeholder}
          className={`border rounded-lg px-4 py-2 focus:outline-none focus:ring-2 transition-all ${
            errors[name] ? 'border-red-500 focus:ring-red-200' : 'border-gray-300 focus:ring-blue-200 focus:border-blue-500'
          }`}
        />
      ) : (
        <input
          type={type}
          name={name}
          value={formData[name]}
          onChange={handleChange}
          placeholder={placeholder}
          className={`border rounded-lg px-4 py-2 focus:outline-none focus:ring-2 transition-all ${
            errors[name] ? 'border-red-500 focus:ring-red-200' : 'border-gray-300 focus:ring-blue-200 focus:border-blue-500'
          }`}
        />
      )}
      {errors[name] && <span className="text-red-500 text-xs mt-1">{errors[name]}</span>}
    </div>
  );

  return (
    <Layout title="Create Products Here" tagline="Create products with ease">
      <div className="max-w-5xl mx-auto mt-10 md:mt-16 p-6 bg-white rounded-xl shadow-lg border border-gray-100 mb-20">
        <div className="border-b pb-4 mb-8 flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-gray-800">Create New Product</h2>
            <p className="text-gray-500 text-sm mt-1">Fill in the details below to add a new product to your inventory.</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <InputField label="Name" name="name" placeholder="Product Name" isRequired={true} />
            <InputField label="Category" name="category" placeholder="e.g. Health, Food" isRequired={true} />
          </div>

          <InputField label="Description" name="description" placeholder="Write a brief description..." isTextarea={true} isRequired={true} />

          <div className="bg-gray-50 p-5 rounded-lg border border-gray-200 grid grid-cols-1 md:grid-cols-2 gap-6">
            <h3 className="col-span-1 md:col-span-2 text-lg font-bold text-gray-700 border-b pb-2 mb-2">Pricing & Units</h3>
            
            {['1', '2', '3'].map((n) => (
              <React.Fragment key={n}>
                <InputField label={`Price ${n} (₹)`} name={`price${n}`} type="number" placeholder={`0.00`} isRequired={n === '1'} />
                <InputField label={`Unit ${n}`} name={`unit${n}`} placeholder={`e.g. 1kg, 500g`} isRequired={n === '1'} />
              </React.Fragment>
            ))}
          </div>

          <div className="flex flex-col">
            <label className="mb-2 text-sm font-semibold text-gray-700">Product Image <span className="text-red-500">*</span></label>
            <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6">
              <div className={`w-40 h-40 rounded-lg overflow-hidden flex items-center justify-center shrink-0 border-4 shadow-sm ${errors.image ? 'border-red-400 bg-red-50' : 'border-gray-100 bg-gray-50'}`}>
                {imagePreview ? (
                  <img src={imagePreview} alt="Preview" className="object-cover w-full h-full" />
                ) : (
                  <span className={`text-xs ${errors.image ? 'text-red-400' : 'text-gray-400'}`}>No Image</span>
                )}
              </div>
              <div className="w-full flex flex-col justify-center h-40">
                <div className={`border-2 border-dashed rounded-lg p-4 flex flex-col items-center justify-center cursor-pointer hover:bg-gray-50 transition-colors h-full ${
                  errors.image ? 'border-red-400 bg-red-50' : 'border-gray-300 bg-white'
                }`}>
                  <input type="file" id="image" name="image" accept="image/*" onChange={handleChange} className="hidden" />
                  <label htmlFor="image" className="cursor-pointer flex flex-col items-center w-full h-full justify-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className={`h-8 w-8 mb-2 ${errors.image ? 'text-red-400' : 'text-blue-500'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a2 2 0 002 2h12a2 2 0 002-2v-1M12 12v9m0-9L9 15m3-3l3 3M12 3v9" />
                    </svg>
                    <span className="text-sm font-medium text-blue-600 hover:text-blue-700">
                      Click to upload image
                    </span>
                  </label>
                </div>
                {errors.image && <span className="text-red-500 text-xs mt-1 text-center">{errors.image}</span>}
              </div>
            </div>
          </div>

          <div className="pt-6 border-t flex justify-end gap-4 mt-8">
            <button
              type="button"
              onClick={() => navigate('/product')}
              className="px-6 py-2.5 rounded-lg font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-8 py-2.5 rounded-lg font-bold text-white bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 shadow-md transition-all"
            >
              {isLoading ? 'Creating...' : 'Create Product'}
            </button>
          </div>

        </form>
      </div>
    </Layout>
  );
};

export default CreateProduct;
